import useGetReturnRequestAvailableCarriers from "@babackapp/private-api-client/client/use-get-return-request-available-carriers";
import useUpdateReturnRequestCarrier from "@babackapp/private-api-client/client/use-update-return-request-carrier";
import type {
  CarrierDto,
  ReturnRequestDto,
} from "@babackapp/private-api-client/types";
import useTranslation from "@babackapp/translations/use-translation";
import { Button, ButtonLoading } from "@babackapp/ui/components/button";
import { Calendar } from "@babackapp/ui/components/calendar";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@babackapp/ui/components/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@babackapp/ui/components/form";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@babackapp/ui/components/popover";
import { TimePicker } from "@babackapp/ui/components/time-picker";
import { cn } from "@babackapp/ui/lib/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { format } from "date-fns";
import { CalendarIcon } from "lucide-react";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { useViewerDateFnsLocale } from "@/lib/translator";

interface ReturnRequestPickupTimeModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  returnRequest: ReturnRequestDto;
  onSuccess?: (returnRequest: ReturnRequestDto) => void;
  onError?: (error: unknown) => void;
}

const pickupTimeSchema = z
  .object({
    date: z.date({
      required_error: "returnDetail.pickupTime.selectDate",
    }),
    startTime: z.string().regex(/^\d{2}:\d{2}$/, "Invalid time format"),
    endTime: z.string().regex(/^\d{2}:\d{2}$/, "Invalid time format"),
  })
  .refine(
    (data) => {
      const [startHour, startMinute] = data.startTime.split(":").map(Number);
      const [endHour, endMinute] = data.endTime.split(":").map(Number);

      const startDateTime = new Date(data.date);
      startDateTime.setHours(startHour, startMinute, 0, 0);

      const endDateTime = new Date(data.date);
      endDateTime.setHours(endHour, endMinute, 0, 0);

      return endDateTime > startDateTime;
    },
    {
      message: "returnDetail.pickupTime.endAfterStart",
      path: ["endTime"],
    },
  );

type PickupTimeFormValues = z.infer<typeof pickupTimeSchema>;

export default function ReturnRequestPickupTimeModal({
  open,
  onOpenChange,
  returnRequest,
  onSuccess,
  onError,
}: ReturnRequestPickupTimeModalProps) {
  const t = useTranslation();
  const dateFnsLocale = useViewerDateFnsLocale();

  const currentPickingTiming = returnRequest.shipping_info.pickingTiming;

  const { data: availableCarriersData } = useGetReturnRequestAvailableCarriers({
    shopId: returnRequest.shopId,
    returnRequestId: returnRequest.id,
  });

  const form = useForm<PickupTimeFormValues>({
    resolver: zodResolver(pickupTimeSchema),
    defaultValues: {
      date: currentPickingTiming?.pickupTimeStart
        ? new Date(currentPickingTiming.pickupTimeStart)
        : new Date(),
      startTime: currentPickingTiming?.pickupTimeStart
        ? format(new Date(currentPickingTiming.pickupTimeStart), "HH:mm")
        : "09:00",
      endTime: currentPickingTiming?.pickupTimeEnd
        ? format(new Date(currentPickingTiming.pickupTimeEnd), "HH:mm")
        : "17:00",
    },
  });

  const { mutate: updateCarrier, isPending } = useUpdateReturnRequestCarrier({
    onSuccess: (updatedReturnRequest) => {
      onSuccess?.(updatedReturnRequest);
      onOpenChange(false);
    },
    onError: (err: unknown) => {
      onError?.(err);
    },
  });

  useEffect(() => {
    if (currentPickingTiming?.pickupTimeStart) {
      form.reset({
        date: new Date(currentPickingTiming.pickupTimeStart),
        startTime: format(
          new Date(currentPickingTiming.pickupTimeStart),
          "HH:mm",
        ),
        endTime: currentPickingTiming?.pickupTimeEnd
          ? format(new Date(currentPickingTiming.pickupTimeEnd), "HH:mm")
          : "17:00",
      });
    }
  }, [currentPickingTiming, form]);

  const onSubmit = (data: PickupTimeFormValues) => {
    const [startHour, startMinute] = data.startTime.split(":").map(Number);
    const [endHour, endMinute] = data.endTime.split(":").map(Number);

    const startDateTime = new Date(data.date);
    startDateTime.setHours(startHour, startMinute, 0, 0);

    const endDateTime = new Date(data.date);
    endDateTime.setHours(endHour, endMinute, 0, 0);

    // Find the current carrier ID from available carriers
    const carriers = (availableCarriersData?.carriers as CarrierDto[]) || [];
    const currentCarrier = carriers.find(
      (c: CarrierDto) => c.code === returnRequest.shipping_info.carrier,
    );

    if (!currentCarrier) {
      console.error("Current carrier not found in available carriers");
      return;
    }

    updateCarrier({
      params: {
        path: {
          shopId: returnRequest.shopId,
          returnRequestId: returnRequest.id,
        },
      },
      body: {
        carrierId: currentCarrier.id,
        pickupTimeStart: startDateTime.getTime(),
        pickupTimeEnd: endDateTime.getTime(),
      } as any, // TODO: Run pnpm codegen to update types
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{t("returnDetail.pickupTime.title")}</DialogTitle>
          <DialogDescription>
            {t("returnDetail.pickupTime.description")}
          </DialogDescription>
          {returnRequest.shipping_info.carrier === "tnt" && (
            <div className="mt-2 rounded-md bg-yellow-50 p-3 text-sm text-yellow-800 border border-yellow-200">
              {t("returnDetail.pickupTime.tntMinimum2Hours")}
            </div>
          )}
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-4 py-4"
          >
            <FormField
              control={form.control}
              name="date"
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>{t("returnDetail.pickupTime.date")}</FormLabel>
                  <Popover>
                    <PopoverTrigger asChild>
                      <FormControl>
                        <Button
                          variant="outline"
                          className={cn(
                            "w-full justify-start text-left font-normal",
                            !field.value && "text-muted-foreground",
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {field.value ? (
                            format(field.value, "PPP", {
                              locale: dateFnsLocale,
                            })
                          ) : (
                            <span>{t("returnDetail.pickupTime.pickDate")}</span>
                          )}
                        </Button>
                      </FormControl>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={field.value}
                        onSelect={field.onChange}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="startTime"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t("returnDetail.pickupTime.startTime")}
                    </FormLabel>
                    <FormControl>
                      <TimePicker {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="endTime"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t("returnDetail.pickupTime.endTime")}
                    </FormLabel>
                    <FormControl>
                      <TimePicker {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <DialogFooter>
              <DialogClose asChild>
                <Button type="button" variant="outline" disabled={isPending}>
                  {t("misc.cancel")}
                </Button>
              </DialogClose>
              <ButtonLoading
                type="submit"
                disabled={isPending}
                isLoading={isPending}
              >
                {t("returnDetail.pickupTime.update")}
              </ButtonLoading>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
